import React from 'react';
import { GuideOpenBody } from '../../../../../interfaces/stay-guide';
import { StatusPill } from '../../../../status-pill/status-pill';
import { StayKey, t } from '../../../../onboarding/stay/stay-strings';
import { AccessNotice } from './access-notice';
import { GuideH1, GuideLink } from './guide-frame';
import { At, langOf, shortDateIn, telHref, timeIn } from './guide-format';
import { GuideSectionId, nowSectionOf, sectionsShown } from './guide-state';
import {
  Avatar,
  CardsBlock,
  CardGrid,
  CardSub,
  CardTitle,
  Hero,
  HeroImage,
  HeroPanel,
  HeroWithPhoto,
  HostRow,
  HostText,
  Panel,
  SecondaryAnchor,
  SectionHeading,
  Stack,
  StayLines,
} from './guide.styled';

const L = 'en' as const;

const CARD: Record<GuideSectionId, { title: StayKey; sub?: StayKey }> = {
  arrival: { title: 'guide.cardArrival', sub: 'guide.cardArrivalSub' },
  essentials: { title: 'guide.cardEssentials', sub: 'guide.cardEssentialsSub' },
  local: { title: 'guide.cardLocal', sub: 'guide.cardLocalSub' },
  checkout: { title: 'guide.cardCheckout', sub: 'guide.cardCheckoutSub' },
  photos: { title: 'guide.cardPhotos' },
};

/**
 * THE HUB — /s/:token/guide (plan S7.7). A welcome (the cover photo behind a
 * glass panel, the display face: a brand moment), the codes' notice while they
 * are held back, the host, the stay's two times, and one card per section —
 * the card for where the guest is in the stay carries "Now". Local tips and
 * Photos have no card when there is nothing in them (no fake content).
 */
export const GuideHub: React.FC<{ body: GuideOpenBody }> = ({ body }) => {
  const { guide, media, window: w, zone } = body;
  const lang = langOf(guide.content_language);
  const first = (body.stay.first_name || '').trim();
  const host = guide.host_name.trim();
  const now = nowSectionOf(body.stage);
  const tel = telHref(guide.host_phone);
  const HeroBox = media.cover ? HeroWithPhoto : Hero;
  const checkoutTime = timeIn(w.check_out_at, zone);

  return (
    <>
      <HeroBox>
        {media.cover ? <HeroImage src={media.cover.url} alt="" /> : null}
        <HeroPanel>
          <GuideH1 hero>
            {first ? t(L, 'guide.welcome', { first }) : t(L, 'guide.welcomeNoName')}
          </GuideH1>
        </HeroPanel>
      </HeroBox>

      {body.access ? null : <AccessNotice notice={body.access_notice} zone={zone} />}

      {host || guide.welcome_message || media.host ? (
        <Panel aria-labelledby="guide-host-name" data-guide-host="">
          <HostRow>
            {media.host ? (
              <Avatar
                src={media.host.url}
                alt={host ? t(L, 'guide.hostPhotoAlt', { host }) : t(L, 'guide.hostPhotoAltNoName')}
              />
            ) : null}
            <Stack>
              <SectionHeading id="guide-host-name">{host || t(L, 'guide.tileHost')}</SectionHeading>
              {guide.welcome_message ? <HostText lang={lang}>{guide.welcome_message}</HostText> : null}
            </Stack>
          </HostRow>
        </Panel>
      ) : null}

      <Panel aria-labelledby="guide-stay-title" data-guide-stay="">
        <SectionHeading id="guide-stay-title">{t(L, 'datesTitle')}</SectionHeading>
        <StayLines>
          <div>
            <dt>{t(L, 'guide.stayCheckIn')}</dt>
            <dd>
              <At iso={w.check_in_at}>
                {t(L, 'guide.dateAtTime', { date: shortDateIn(w.check_in_at, zone), time: timeIn(w.check_in_at, zone) })}
              </At>
            </dd>
          </div>
          <div>
            <dt>{t(L, 'guide.stayCheckOut')}</dt>
            <dd>
              <At iso={w.check_out_at}>
                {t(L, 'guide.dateAtTime', { date: shortDateIn(w.check_out_at, zone), time: checkoutTime })}
              </At>
            </dd>
          </div>
        </StayLines>
        {tel ? (
          <SecondaryAnchor href={tel}>{t(L, 'guide.callHost', { host: host || t(L, 'guide.yourHost') })}</SecondaryAnchor>
        ) : null}
      </Panel>

      <CardsBlock>
        <SectionHeading>{t(L, 'guide.navLabel')}</SectionHeading>
        <CardGrid>
          {sectionsShown(body).map(id => {
            const card = CARD[id];

            return (
              <li key={id} data-guide-card={id} data-now={id === now || undefined}>
                <GuideLink target={id} kind="card">
                  <CardTitle>{t(L, card.title)}</CardTitle>
                  {card.sub ? (
                    <CardSub>
                      {id === 'checkout' ? (
                        <>
                          {t(L, card.sub).split('{time}')[0]}
                          <At iso={w.check_out_at}>{checkoutTime}</At>
                          {t(L, card.sub).split('{time}')[1]}
                        </>
                      ) : (
                        t(L, card.sub)
                      )}
                    </CardSub>
                  ) : null}
                  {id === now ? (
                    <StatusPill tone="brand" dot>
                      {t(L, 'guide.now')}
                    </StatusPill>
                  ) : null}
                </GuideLink>
              </li>
            );
          })}
        </CardGrid>
      </CardsBlock>
    </>
  );
};
